import { BlobApiImpl } from '../api/blob-api-impl';
import { DataAggregationApiImplementation } from '../api/supplier-api-dataaggregation-implementation';
import {
  DealerApiCommunications,
  redirectToLoginPage,
  setDealerTokenProvider
} from '../../webmodule-common/other/api/dealer-api-communications';
import { dealerVerifyUser, performPageLoadLoginVerifications } from '../api/dealer-pageload-verifications';
import { displayAllError } from '../../webmodule-common/other/ui/modal-errorhandler';
import { EventSnippet } from '../../webmodule-common/interop/types/misc';
import { FranchiseeNetworkApiImplementation } from '../api/franchisee-network-api-implementation';
import { getApi, setApiFactory, setApiInjector } from '../api/api-injector';
import { getCurrentUser, setAfterApplyUserEvent } from '../../webmodule-common/other/api/current-user';
import { getUserLock } from './common/optimistic-user-lock';
import { registerComponents } from '../../webmodule-common/components/src/components/register-components';
import { registerComponentsLocal } from '../../webmodule-common/other/ui/templatecontrols/component-registry';
import { registerLangFile, tlang } from '../../webmodule-common/other/language/lang';
import { registerTechnicalDictionary } from '../../webmodule-common/other/language/technical-dictionary';
import { responseHandler } from '../api/api-response-handler';
import { runGlobalInit } from '../../webmodule-common/other/app-global-init';
import { ApiError, ProblemTypes } from '../../webmodule-common/interop/problem-details';
import { setCacheRegistry } from './cache-impl/cache-registry';
import { setErrorDialogEventHandler, showError } from '../../webmodule-common/other/ui/show-error';
import { SupplierApiImplementation } from '../api/supplier-api-implementation';
import { userDataStore } from './common/current-user-data-store';
import { UserProfileCache } from './cache-impl/user-profile-cache';

import { appConfig } from './app-config';
import { bindApplicationFeatures } from '../../webmodule-common/other/bind-application-features';
import {
  clearCurrentUserFromSession,
  saveCurrentUserIntoSession,
  verifySession
} from '../../webmodule-common/other/user-session-verifier';
import { ConnectedState } from '../api/misc-codegen';
import { DefaultLangBuilder } from '../../localization/default-lang-builder';
import { GlobalNotificationManager } from '../../webmodule-common/other/ui/icons/icon-notification-signal';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { initOfflineOverlaySystem } from '../../webmodule-common/other/ui/offline-overlay';
import { initPageResumeHandler } from './common/page-resume-integration';
import { interpolate } from '../../localization/global';
import { intializeLocalizationServices } from '../../localization/initialize-services';
import { setLocalizeInjector } from '../../webmodule-common/components/src/common/localize-injector';
import { setRoutes } from '../../webmodule-common/other/router';
import { showDevelopmentError } from '../../webmodule-common/other/development-error';
import { WMEventSource } from '../api/event-source';
import { WMEventSourceClient } from '../../webmodule-common/other/general/event-source-client';

let _appStartupEvents: Promise<void> | undefined;

export function getApplicationStartupEvents() {
  if (_appStartupEvents === undefined) {
    _appStartupEvents = applicationStartupEvents();
  }
  return _appStartupEvents;
}

globalThis.getApplicationStartupEvents = getApplicationStartupEvents;

async function applicationStartupEvents() {
  const log = (msg: string) => console.log('STARTUP: ' + msg);
  dealerBuildApplicationBindings();
  if (!(await dealerVerifyUser(log))) {
    window.location.reload();
  }
  await dealerPrepUserData(log);
}

async function dealerPrepUserData(_updateNote: (msg: string) => void) {
  try {
    //we want to enforce that the system the user connects to is fully up to date. this is a
    //simple call to the v6 systems to check their version, and will ensure the system resets info
    //as needed. This is also required for the quotes page, so we know if we are a multi supplier view or not
  } catch {
    console.log('could not load suppliers');
  }
}

async function connectUserAfterLogin() {
  const reset = async () => {
    userDataStore.clear();
    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');
    redirectToLoginPage();
  };
  const user = getCurrentUser();
  if (user !== null) {
    if (!(await verifySession())) {
      await reset();
      return;
    }

    //DO NOT MAKE ANY CALLS THAT NEED AUTHORIZATION BEFORE CONNECTING THE USER
    //Make any api calls or anything else here that are necessary to be used for the current user
    try {
      const state = await getApi().post<ConnectedState>('api/Supplier/ConnectUser', {});
      if (!state?.connected) {
        await information(
          tlang`Your login details are valid, but the server refused to accept your login at this time. please try again later.`,
          tlang`Login Rejected`
        );
        await reset();
        return;
      }
      //this will update the user claims security before we rebind the router.
      await userDataStore.loadCoreDetailsAfterLogin();
      saveCurrentUserIntoSession();
    } catch (e) {
      await showDevelopmentError(e as Error);
      await reset();
    }
  } else {
    clearCurrentUserFromSession();
    localStorage.removeItem('PAT-in-use');
    userDataStore.clear();
    redirectToLoginPage();
    //we always want to login again
  }
}

export function rebindRouter() {
  const config = appConfig();
  setRoutes(config.routes);
  /*
        addURLResolvers(
            config.routes
                .filter((x) => x.resolveUrl !== undefined)
                .map((x) => {
                    return x.resolveUrl as ResolveURLEntry;
                })
        );
        */
}

function dealerBuildApplicationBindings() {
  setLocalizeInjector({
    interpolate: (id: string, data?: Record<string, any>) => {
      return interpolate(id, data);
    }
  });
  intializeLocalizationServices(
    globalThis.dealerConfiguration.languages,
    globalThis.dealerConfiguration.glossary,
    globalThis.dealerConfiguration.localizationFiles,
    () => DefaultLangBuilder.supplierDefault()
  );

  registerTechnicalDictionary(globalThis.dealerConfiguration.dictItems);
  registerLangFile(globalThis.dealerConfiguration.langFile);

  registerComponents();
  registerComponentsLocal();

  bindApplicationFeatures();

  setAfterApplyUserEvent(connectUserAfterLogin);
  registerEventSourceClients();
  setDealerTokenProvider(() => getUserLock());

  const errorDialogEventHandler = async (item: Error | ApiError, title: EventSnippet) => {
    try {
      await displayAllError(title, item);
    } catch (e) {
      console.log(e);
    }
  };
  setErrorDialogEventHandler(errorDialogEventHandler);

  setApiFactory({
    supplier: () => new SupplierApiImplementation(getApi()),
    franchisee: () => new FranchiseeNetworkApiImplementation(getApi()),
    blob: () => new BlobApiImpl(getApi()),
    dataAggregation: () => new DataAggregationApiImplementation(getApi())
  });

  let _commSingleton: DealerApiCommunications | undefined;
  const apiInjecterEvent = () => {
    if (!_commSingleton)
      _commSingleton = new DealerApiCommunications('', responseHandler, () => {
        //Redirect to home page, next query will force a login to occur
        window.location.href = '/login';
      });
    return _commSingleton;
  };
  //Dependency inject an api for the entire application
  setApiInjector(apiInjecterEvent);
  setCacheRegistry(() => {
    const api = getApi();
    return {
      userProfile: new UserProfileCache(api)
    };
  });

  window.addEventListener('unhandledrejection', function (event) {
    if (event.reason.message.includes('Vaadin')) {
      return;
    }
    console.error(event.reason.stack);
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    showError(
      new ApiError({
        type: ProblemTypes.InternalError,
        title: tlang`unhandled error`,
        status: 0,
        detail: event.reason.message,
        stackTrace: event.reason.stack
      }),
      () => tlang`Unhandled Error inside a promise occurred`
    );
  });

  runGlobalInit();

  // Initialize the page resume handler for browser lifecycle recovery
  // (tab visibility, browser wake from sleep, etc.)
  initPageResumeHandler();
}
function registerEventSourceClients() {
  //configure main event source.
  WMEventSourceClient.getInstance(undefined, {
    onlineCallback: value => {
      GlobalNotificationManager.getInstance().online = value;
      if (globalThis.dealerConfiguration.apiHost === globalThis.dealerConfiguration.v6apiHost)
        GlobalNotificationManager.getInstance('v6config').online = value;
    },
    afterOnlineEvent: () => {
      WMEventSource.afterConnect();
    },
    url: globalThis.dealerConfiguration.apiHost
  });

  // Initialize the offline overlay system to automatically show/hide based on connection state
  initOfflineOverlaySystem();
}

if (!performPageLoadLoginVerifications) console.log('missing page loader');
